import React from 'react';
import { createPortal } from 'react-dom';
import { EXTERNAL_VALIDATION_THRESHOLDS, DATABASE_SYNC_THRESHOLDS } from '../config/thresholds';
// Local SVG assets are now in public/assets/
import flareLogo from '../assets/flare-logo.svg';
import songbirdLogo from '../assets/songbird-logo.svg';
import bothLogo from '../assets/both-logo.svg';

export const calculateSubmissionsAverage = (entityData) => {
  if (!entityData) return 0;
  
  const allPercentages = [
    entityData?.submit1?.['15min']?.percentage || 0,
    entityData?.submit1?.['2hr']?.percentage || 0,
    entityData?.submit1?.['6hr']?.percentage || 0,
    entityData?.submit2?.['15min']?.percentage || 0,
    entityData?.submit2?.['2hr']?.percentage || 0,
    entityData?.submit2?.['6hr']?.percentage || 0,
    entityData?.submitSignatures?.['15min']?.percentage || 0,
    entityData?.submitSignatures?.['2hr']?.percentage || 0,
    entityData?.submitSignatures?.['6hr']?.percentage || 0
  ];
  
  const average = allPercentages.reduce((acc, val) => acc + val, 0) / allPercentages.length;
  return Math.round(average * 10) / 10;
};

export const calculateResultsIssues = (metricsData) => {
  if (!metricsData) return 0;
  
  let totalIssues = 0;
  
  Object.entries(metricsData).forEach(([timeframe, data]) => {
    if (data) {
      totalIssues += (data.late || 0) + (data.missing || 0) + (data.invalid || 0);
    }
  });
  
  return totalIssues;
};

export const getIssuesColor = (issues) => {
  if (issues === 0) return 'color-green';
  if (issues <= 2) return 'color-amber';
  return 'color-red';
};
export const StatusCellRenderer = (props) => {
    const status = props.value;
    const method = props.colDef.field;
    const txLink = props.data[`${method}_tx_link`];
    let statusClass = '';
  
    switch (status?.toUpperCase()) {
      case 'SENT':
      case 'RECEIVED':
      case 'SUCCESS':
        statusClass = 'status-success';
        break;
      case 'SENT (LATE)':
      case 'SENT (EARLY)':
      case 'LATE':
        statusClass = 'status-late';
        break;
      case 'PENDING':
        statusClass = 'status-pending';
        break;
      case 'FAILED':
      case 'MISSED':
      case 'DUPLICATE':
      case 'MISSING':
      case 'ERRORS':
      case 'GAPS':
      case 'ZERO VALUES':
        statusClass = 'status-missing';
        break;
      default:
        statusClass = 'status-missing';
    }
  
    return (
      <div className="status-cell-wrapper">
        <div className={`status-cell ${statusClass}`}>
          {txLink ? <a href={txLink} target="_blank" rel="noopener noreferrer">{status}</a> : status}
        </div>
      </div>
    );
};
  
export const NodeStatusCellRenderer = (props) => {
  let status = props.value;
  const networkKey = (props?.data?.network || '').toString().toUpperCase();

  // XRPL special case: treat as syncing until coverage meets goal
  if (networkKey.startsWith('XRPL')) {
    const goal = Number(props?.data?.history_goal_days);
    const coverage = Number(props?.data?.history_coverage_days);
    if (!Number.isNaN(goal) && !Number.isNaN(coverage) && coverage < goal) {
      status = 'syncing';
    }
  }

  let statusClass = '';

  // Check if this is a database sync status (contains "BEHIND" and we have chain_latest_block)
  const isDatabaseSync = status?.includes('BEHIND') && props?.data?.chain_latest_block;
  // Chain view "N BEHIND" (no chain_latest_block) should also be threshold-driven
  const isChainBehind = status?.includes('BEHIND') && !props?.data?.chain_latest_block;
  
  if (isDatabaseSync) {
    // Extract blocks behind from status text (e.g., "5 BEHIND" -> 5)
    const match = status.match(/(\d+)\s+BEHIND/);
    if (match) {
      const blocksBehind = parseInt(match[1]);
      
      // Map network to threshold key
      let thresholdKey = networkKey;
      if (networkKey === 'BITCOIN') thresholdKey = 'BTC';
      else if (networkKey === 'DOGECOIN') thresholdKey = 'DOGE';
      
      const thresholds = DATABASE_SYNC_THRESHOLDS[thresholdKey] || DATABASE_SYNC_THRESHOLDS.DEFAULT;
      
      if (blocksBehind <= thresholds.GREEN_MAX) {
        statusClass = 'status-success'; // GREEN
      } else if (blocksBehind <= thresholds.AMBER_MAX) {
        statusClass = 'status-late'; // AMBER
      } else {
        statusClass = 'status-missing'; // RED
      }
    } else {
      statusClass = 'status-missing'; // Default to red if can't parse
    }
  } else if (isChainBehind) {
    // Chain-view N BEHIND → use EXTERNAL_VALIDATION_THRESHOLDS to color like the dot
    const match = status.match(/(\d+)\s+BEHIND/);
    if (match) {
      const blocksBehind = parseInt(match[1]);

      // Map network to external validation threshold key
      let extKey = networkKey;
      if (networkKey === 'BITCOIN') extKey = 'BTC';
      else if (networkKey === 'DOGECOIN') extKey = 'DOGE';
      else if (networkKey === 'ETHEREUM') extKey = 'ETH';
      else if (networkKey === 'FLARE' || networkKey === 'FLARE_OBS' || networkKey === 'FLARE_VAL') extKey = 'FLR';
      else if (networkKey === 'SONGBIRD' || networkKey === 'SONGBIRD_OBS') extKey = 'SGB';
      else if (networkKey.startsWith('XRPL')) extKey = 'XRPL';

      const thresholds = EXTERNAL_VALIDATION_THRESHOLDS[extKey] || EXTERNAL_VALIDATION_THRESHOLDS.DEFAULT;

      if (blocksBehind <= thresholds.GREEN_MAX) {
        statusClass = 'status-success';
      } else if (blocksBehind <= thresholds.AMBER_MAX) {
        statusClass = 'status-late';
      } else {
        statusClass = 'status-missing';
      }
    } else {
      statusClass = 'status-missing';
    }
  } else {
    // Original logic for non-database sync statuses
    switch (status?.toLowerCase()) {
      case 'synced':
        statusClass = 'status-success';
        break;
      case 'syncing':
        // Map syncing to red per updated policy
        statusClass = 'status-missing';
        break;
      case 'healthy':
        statusClass = 'status-healthy';
        break;
      case 'indexed':
        statusClass = 'status-indexed';
        break;
      case 'indexing':
        statusClass = 'status-indexing';
        break;
      default:
        statusClass = 'status-missing';
    }
  }

  return (
    <div className="status-cell-wrapper">
      <div className={`status-cell ${statusClass}`}>{status}</div>
    </div>
  );
};



export const NetworkCellRenderer = (props) => {
  const network = props.value;
  let logoUrl;

  switch (network.toLowerCase()) {
    case 'ethereum':
      logoUrl = 'https://cdn.prod.website-files.com/64eb6710f68f6b333f8886a6/64f9ee18d839dd5236891065_ethereum-eth.svg';
      break;
    case 'xrpl':
      logoUrl = 'https://cdn.prod.website-files.com/64eb6710f68f6b333f8886a6/67488e2fde4cd0176fd45841_xrp-xrp.svg';
      break;
    case 'bitcoin':
      logoUrl = 'https://cdn.prod.website-files.com/64eb6710f68f6b333f8886a6/67488e303fbeb29d6c152423_bitcoin%20(1).svg';
      break;
    case 'flare':
      logoUrl = flareLogo;
      break;
    case 'songbird':
      logoUrl = songbirdLogo;
      break;
    case 'both':
      logoUrl = bothLogo;
      break;
    case 'dogecoin':
      logoUrl = 'https://cdn.prod.website-files.com/64eb6710f68f6b333f8886a6/64f9ee184ba0bfd74b4339ae_dogecoin.svg';
      break;
    default:
      logoUrl = '';
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {logoUrl && (
        <img
          src={logoUrl}
          alt={network}
          style={{ width: '18px', marginRight: '15px' }}
          decoding="async"
          onError={(e) => {
            // Hide broken images gracefully
            e.target.style.display = 'none';
          }}
        />
      )}
      {network}
    </div>
  );
};

export const EntityCellRenderer = (props) => {
  const entity = props.value;
  const size = Number(props.size) || 18; // outer circle size
  const showText = props.showText !== false; // show entity text by default
  let logoUrl = '';

  // Prefix-match so entity variants (e.g. ENV_BACKUP for DB-only backup hosts)
  // reuse the correct parent-entity logo without us having to enumerate every
  // new suffix here. An exact "env"/"uys" still matches.
  const entityLc = (entity || '').toString().toLowerCase();
  if (entityLc === 'uys' || entityLc.startsWith('uys_') || entityLc.startsWith('uys-')) {
      logoUrl = 'https://raw.githubusercontent.com/TowoLabs/ftso-signal-providers/master/assets/0x53CAEDDA4339eD74272ECfEF85b657dEf18fA2e4.png';
  } else if (entityLc === 'env' || entityLc.startsWith('env_') || entityLc.startsWith('env-')) {
      logoUrl = 'https://raw.githubusercontent.com/TowoLabs/ftso-signal-providers/master/assets/0x58Cd43E9FcdBd4D0F507aB4f6029dB8032746da8.png';
  }
  
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {logoUrl && (
        <div
          style={{
            width: `${size}px`,
            height: `${size}px`,
            borderRadius: '50%',
            backgroundColor: 'white',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginRight: '12px',
            overflow: 'hidden',
          }}
        >
          <img
            src={logoUrl}
            alt={entity}
            style={{
              width: `${Math.round(size * 0.78)}px`,
              height: `${Math.round(size * 0.78)}px`,
              objectFit: 'contain',
            }}
          />
        </div>
      )}
      {showText ? entity : null}
    </div>
  );
};

export const FDCAttestationStatusRenderer = (props) => {
  const attestations = props.value || [];
  const [showModal, setShowModal] = React.useState(false);
  const [modalPosition, setModalPosition] = React.useState({ x: 0, y: 0 });
  

  
  if (!attestations || attestations.length === 0) {
    return <div className="fdc-attestation-status">No attestations</div>;
  }

  const handleMouseEnter = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setModalPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
    setShowModal(true);
  };

  const handleMouseLeave = () => {
    setShowModal(false);
  };

  return (
    <>
      <div 
        className="fdc-attestation-status"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: 'pointer' }}
      >
        {attestations.map((attestation, index) => (
          <div
            key={index}
            className={`fdc-status-circle ${attestation.bitvote_result ? 'valid' : 'invalid'}`}
          />
        ))}
      </div>
      
      {showModal && createPortal(
        <div 
          style={{
            position: 'fixed',
            left: `${modalPosition.x}px`,
            top: `${modalPosition.y}px`,
            transform: 'translateX(-50%) translateY(-100%)',
            zIndex: 9999,
            minWidth: '220px',
            maxWidth: '320px',
            padding: '16px',
            backgroundColor: '#2d3748',
            border: '1px solid #4a5568',
            borderRadius: '12px',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
            color: '#e2e8f0',
            fontSize: '13px',
            pointerEvents: 'none'
          }}
        >
          <div style={{ 
            fontSize: '14px', 
            fontWeight: '600', 
            marginBottom: '12px', 
            color: '#f7fafc',
            textAlign: 'center',
            borderBottom: '1px solid #4a5568',
            paddingBottom: '8px'
          }}>
            Attestation Details
          </div>
          {attestations.map((attestation, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '8px',
              padding: '6px 8px',
              borderRadius: '6px',
              background: 'rgba(255, 255, 255, 0.05)'
            }}>
              <div
                className={`fdc-status-circle ${attestation.bitvote_result ? 'valid' : 'invalid'}`}
                style={{ marginRight: '12px', minWidth: '12px', flexShrink: 0 }}
              />
              <div>
                <span style={{ fontWeight: '500', color: '#e2e8f0' }}>
                  {attestation.attestation_type}
                </span>
                {' '}
                <span style={{ color: '#a0aec0' }}>
                  ({attestation.source_id})
                </span>
              </div>
            </div>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

export const HitRateCellRenderer = (props) => {
  const currentValue = props.value;
  const currentRound = props.data.voting_round_id;
  const entity = props.data.entity;
  const network = props.data.network;
  const resultsStatus = props.data.Results;

  // Show blue PENDING pill whenever hit rate isn't ready yet
  const isResultsPending = String(resultsStatus || '').toUpperCase() === 'PENDING';
  const isHrMissing = currentValue == null || Number.isNaN(currentValue);
  if (isResultsPending || isHrMissing) {
    return (
      <div className="status-cell-wrapper">
        <div className={`status-cell status-pending`}>PENDING</div>
      </div>
    );
  }
  
  const previousRoundData = props.api.getRowNode(
      `${entity}-${network}-${currentRound - 1}`
  )?.data;
  
  const previousValue = previousRoundData?.hit_rate;
  
  let trendIcon = null;
  if (previousValue != null && currentValue != null) {
      const containerStyle = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '26px',
      height: '26px',
      };
  
      if (currentValue > previousValue) {
      trendIcon = (
          <div style={containerStyle}>
          <img 
              src="https://cdn.prod.website-files.com/64eb6710f68f6b333f8886a6/6718d9fd3a41aefad2e380ac_growth.svg"
              alt="Increase"
              style={{ width: '26px' }}
          />
          </div>
      );
      } else if (currentValue < previousValue) {
      trendIcon = (
          <div style={containerStyle}>
          <img 
              src="https://cdn.prod.website-files.com/64eb6710f68f6b333f8886a6/6718d9fd98e83149b8dae81a_decrees.svg"
              alt="Decrease"
              style={{ 
              width: '26px',
              }}
          />
          </div>
      );
      } else {
      trendIcon = (
          <div style={containerStyle}>
          <div 
              style={{ 
              width: '20px',
              height: '1px',
              backgroundColor: '#fbbf24',
              }}
          />
          </div>
      );
      }
  }
  
  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      width: '100%'
    }}>
      <span style={{ textAlign: 'center', minWidth: '34px' }}>{`${Number(currentValue).toFixed(0)}%`}</span>
      {trendIcon}
    </div>
  );
};